import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { RefreshCw, ChevronDown, X, AlertTriangle } from 'lucide-react';
import workflowService, { ScheduledJobType, type AutomationRule } from '@/services/workflow.service';
import n8nService, { N8nWorkflow } from '@/services/n8n.service';
import { teamMembersService, TeamMember } from '@/services/team-members.service';
import { useAuthStore } from '@stores/authStore';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface JobConfigPanelProps {
  jobType: ScheduledJobType;
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  // True only for the Create modal. Report Generation pre-selects the signed-in
  // admin as a recipient on create; on edit it must NOT, because silently adding
  // a recipient to a saved job changes who receives it (and would mark the edit
  // form dirty against its untouched snapshot).
  isCreate?: boolean;
}

// Required config fields per job type
const REQUIRED_FIELDS: Record<string, string[]> = {
  [ScheduledJobType.AUTOMATION_RULE]: ['ruleId'],
  [ScheduledJobType.N8N_WORKFLOW]: ['workflowId'],
  [ScheduledJobType.SLA_CHECK]: [],
  [ScheduledJobType.STALE_TICKET_CHECK]: ['staleAfterHours'],
  // REPORT_GENERATION is validated specially in getMissingJobConfigFields
  // (delivery method + conditional recipients), not via this simple list.
  [ScheduledJobType.REPORT_GENERATION]: [],
  [ScheduledJobType.CLEANUP]: ['target', 'retentionDays'],
  [ScheduledJobType.CUSTOM]: ['webhookUrl'],
};

export function getDefaultJobConfig(jobType: ScheduledJobType): Record<string, unknown> {
  switch (jobType) {
    case ScheduledJobType.AUTOMATION_RULE:
      return { ruleId: '' };
    case ScheduledJobType.N8N_WORKFLOW:
      return { workflowId: '', payload: {} };
    case ScheduledJobType.SLA_CHECK:
      return {};
    case ScheduledJobType.STALE_TICKET_CHECK:
      return { staleAfterHours: 24, statuses: '', action: 'notify' };
    case ScheduledJobType.REPORT_GENERATION:
      return { reportType: '', deliverEmail: true, deliverUi: false, recipients: '', recipientIds: [] };
    case ScheduledJobType.CLEANUP:
      return { target: 'executions', retentionDays: 30 };
    case ScheduledJobType.CUSTOM:
      return { webhookUrl: '', method: 'POST', headers: {}, payload: {} };
    default:
      return {};
  }
}

export function getMissingJobConfigFields(jobType: ScheduledJobType, config: Record<string, unknown>): string[] {
  // Report Generation has conditional requirements: a report type, at least one
  // delivery method, and recipients only when Email delivery is on.
  if (jobType === ScheduledJobType.REPORT_GENERATION) {
    const missing: string[] = [];
    if (!config.reportType || String(config.reportType).trim() === '') {
      missing.push('reportType');
    }
    const deliverEmail = config.deliverEmail !== false; // default on
    const deliverUi = config.deliverUi === true;
    if (!deliverEmail && !deliverUi) {
      missing.push('a delivery method (Email or Reports page)');
    }
    if (deliverEmail && (!config.recipients || String(config.recipients).trim() === '')) {
      missing.push('recipients');
    }
    return missing;
  }

  const required = REQUIRED_FIELDS[jobType] || [];
  return required.filter(
    (field) => !config[field] || (typeof config[field] === 'string' && (config[field] as string).trim() === '')
  );
}

// Shared styles
const inputClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500';
const selectClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white';
const textareaClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 px-3 py-1.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 resize-y dark:placeholder-gray-500';
const labelClass = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

function ConfigField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

// Valid active ticket statuses that a Stale Ticket Check can target. `resolved`
// is terminal and never eligible, so it is intentionally excluded.
const STALE_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending', label: 'Pending' },
];

// The `statuses` config is stored as a comma-separated string (what the backend
// consumer parses). Normalize either shape into a lowercase list for the UI.
function parseStatusList(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw.map((s) => String(s))
    : typeof raw === 'string'
      ? raw.split(',')
      : [];
  return list.map((s) => s.trim().toLowerCase()).filter((s) => s.length > 0);
}

// Automation Rule job config: pick one of the tenant's active automation rules.
//
// This used to be a free-text "Automation Rule ID" box, which meant the rule's
// raw UUID had to be exposed in the Automations tab purely so it could be copied
// across. Selecting from the live rule list removes both the copy/paste step and
// the reason to surface an internal id in the UI.
function AutomationRuleJobConfig({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}) {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(false);
  const { open, setOpen, containerRef, triggerRef, listClass, maxHeight } = useAnchoredDropdown();

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const response = await workflowService.getRules({ isActive: true, limit: 100 });
      // Only TIME_BASED rules can run on a schedule: a scheduled job fires an
      // event with no ticket in context, so the backend rejects anything else
      // on save. Offer only those here rather than letting the user pick a rule
      // the server will 400 (#20582 / BUG-059). The shared frontend TriggerType
      // enum is out of sync with the API (it has 'scheduled'; the API returns
      // the Prisma value 'TIME_BASED'), so match the backend value directly —
      // this is why #18955 could not filter by the enum without emptying the list.
      const timeBased = (response.data || []).filter(
        (r) => String(r.triggerType).toUpperCase() === 'TIME_BASED',
      );
      setRules(timeBased);
    } catch (error) {
      console.error('Failed to load automation rules:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const selectedId = (config.ruleId as string) || '';
  const selectedRule = rules.find((r) => r.id === selectedId);

  return (
    <div className="mt-3 space-y-2">
      <ConfigField label="Automation Rule *">
        <div className="flex gap-2">
          <div className="relative flex-1" ref={containerRef}>
            <button
              type="button"
              ref={triggerRef}
              onClick={() => setOpen(!open)}
              className={`${selectClass} flex items-center justify-between text-left`}
            >
              <span className={selectedRule ? '' : 'text-gray-400 dark:text-gray-500'}>
                {loading
                  ? 'Loading rules...'
                  : selectedRule
                    ? selectedRule.name
                    : // A job saved against a rule that has since been deleted or
                      // deactivated would otherwise look like nothing was ever
                      // chosen. Say so instead.
                      selectedId
                      ? 'Selected rule is no longer active'
                      : 'Select an automation rule...'}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''} dark:text-gray-500`}
              />
            </button>
            {open && (
              <div role="listbox" aria-label="Automation rules" style={{ maxHeight }} className={listClass}>
                {loading ? (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Loading rules...
                  </div>
                ) : rules.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No time-based automation rules. Only time-based rules can run on a
                    schedule — create one in Settings &rsaquo; Automations first.
                  </div>
                ) : (
                  rules.map((rule) => (
                    <button
                      key={rule.id}
                      type="button"
                      onClick={() => {
                        onChange({ ...config, ruleId: rule.id });
                        setOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                        selectedId === rule.id
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      <span className="font-medium">{rule.name}</span>
                      {rule.description && (
                        <p className="text-xs text-gray-500 mt-0.5 dark:text-gray-400 line-clamp-1">
                          {rule.description}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={loadRules}
            className="px-2 py-1.5 text-gray-500 hover:text-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-400"
            title="Refresh rules"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {selectedId && !selectedRule && !loading && (
          <p className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" />
            The rule this job points at is inactive or was deleted — it will not run until you pick
            another.
          </p>
        )}
        {/* #18955 — a scheduled job runs with no ticket in context, so only
            time-based rules can run here. The backend rejects anything else on
            save; surface that up front so it isn't a surprise. */}
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Only <span className="font-medium">time-based</span> automation rules can be scheduled.
          A rule triggered by ticket, chat or message events needs a ticket in context and will be
          rejected.
        </p>
      </ConfigField>
    </div>
  );
}

// N8N Workflow Job Config Component
function N8nWorkflowJobConfig({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}) {
  const [workflows, setWorkflows] = useState<N8nWorkflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  const update = (field: string, value: unknown) => {
    onChange({ ...config, [field]: value });
  };

  const loadWorkflows = async () => {
    setLoading(true);
    try {
      const response = await n8nService.getWorkflows({ isActive: true, limit: 100 });
      setWorkflows(response.data || []);
    } catch (error) {
      console.error('Failed to load n8n workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const selectedWorkflow = workflows.find((w) => w.id === config.workflowId);

  return (
    <div className="mt-3 space-y-2">
      <ConfigField label="n8n Workflow *">
        <div className="flex gap-2">
          {!manualMode ? (
            <div className="relative flex-1">
              <button
                type="button"
                onClick={() => setShowDropdown(!showDropdown)}
                className={`${selectClass} flex items-center justify-between text-left`}
              >
                <span className={selectedWorkflow ? '' : 'text-gray-400 dark:text-gray-500'}>
                  {selectedWorkflow ? selectedWorkflow.name : 'Select a workflow...'}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''} dark:text-gray-500`} />
              </button>
              {showDropdown && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowDropdown(false)} />
                  <div className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {loading ? (
                      <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2 dark:text-gray-400">
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Loading workflows...
                      </div>
                    ) : workflows.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No active workflows found.{' '}
                        <button
                          type="button"
                          onClick={() => setManualMode(true)}
                          className="text-primary-600 hover:underline"
                        >
                          Enter ID manually
                        </button>
                      </div>
                    ) : (
                      workflows.map((wf) => (
                        <button
                          key={wf.id}
                          type="button"
                          onClick={() => {
                            update('workflowId', wf.id);
                            setShowDropdown(false);
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                            config.workflowId === wf.id
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'text-gray-900 dark:text-white'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${wf.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                            <span className="font-medium">{wf.name}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 ml-4 dark:text-gray-400">
                            n8n ID: {wf.n8nWorkflowId}
                          </p>
                        </button>
                      ))
                    )}
                    <div className="border-t border-gray-100 dark:border-gray-600 px-3 py-2">
                      <button
                        type="button"
                        onClick={() => {
                          setManualMode(true);
                          setShowDropdown(false);
                        }}
                        className="text-xs text-primary-600 hover:underline"
                      >
                        Enter workflow ID manually
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <input
              type="text"
              className={`${inputClass} flex-1`}
              placeholder="Enter workflow ID (from Settings > n8n)"
              value={(config.workflowId as string) || ''}
              onChange={(e) => update('workflowId', e.target.value)}
            />
          )}
          <button
            type="button"
            onClick={() => {
              if (manualMode) {
                setManualMode(false);
              } else {
                loadWorkflows();
              }
            }}
            className="px-2 py-1.5 text-gray-500 hover:text-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-400"
            title={manualMode ? 'Switch to dropdown' : 'Refresh workflows'}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        {selectedWorkflow && (
          <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
            n8n Workflow ID: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{selectedWorkflow.n8nWorkflowId}</code>
          </p>
        )}
      </ConfigField>
      <ConfigField label="Payload (JSON)">
        <textarea
          className={textareaClass}
          rows={3}
          placeholder='{"key": "value"}'
          value={
            typeof config.payload === 'string'
              ? config.payload
              : JSON.stringify(config.payload || {}, null, 2)
          }
          onChange={(e) => {
            try {
              update('payload', JSON.parse(e.target.value));
            } catch {
              update('payload', e.target.value);
            }
          }}
        />
      </ConfigField>
    </div>
  );
}

const REPORT_TYPE_OPTIONS = [
  { value: 'daily_summary', label: 'Daily Summary' },
  { value: 'weekly_summary', label: 'Weekly Summary' },
  { value: 'sla_compliance', label: 'SLA Compliance' },
  { value: 'agent_performance', label: 'Agent Performance' },
  { value: 'ticket_volume', label: 'Ticket Volume' },
];

// ---- Report recipients ----
//
// `recipients` (a comma-separated email string) is the ONLY thing the backend
// sends to — workflow-service `parseRecipients` splits it and analytics-service
// mails one copy per entry. So it is the single source of truth here, and the
// checkbox state is DERIVED from it.
//
// It used to be the other way round: `recipientIds` drove the UI while
// `recipients` drove delivery, maintained as two independent writes. Deleting a
// team member desynced them permanently — the removed user disappeared from the
// active-member list (so the UI showed one recipient) while their address stayed
// in `recipients` forever (so the job kept mailing two). Deriving one from the
// other makes that state unrepresentable.

const normalizeEmail = (email: string) => email.trim().toLowerCase();

/** Split a stored `recipients` value (string or array) into de-duped addresses. */
export function parseRecipientEmails(raw: unknown): string[] {
  const list = Array.isArray(raw)
    ? raw.map((r) => String(r))
    : typeof raw === 'string'
      ? raw.split(',')
      : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const entry of list) {
    const email = entry.trim();
    if (!email) continue;
    const key = normalizeEmail(email);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(email);
  }
  return out;
}

export interface RecipientSelection {
  email: string;
  /** The active team member this address belongs to, when one still exists. */
  member?: TeamMember;
}

/**
 * Pair every stored address with its active member. An address with no match is
 * an orphan — typically a user who was deleted or deactivated after the job was
 * saved. Orphans are kept (never silently dropped) so they stay visible and
 * removable instead of mailing invisibly forever.
 */
export function resolveRecipientSelection(
  raw: unknown,
  members: TeamMember[],
): RecipientSelection[] {
  const byEmail = new Map(members.map((m) => [normalizeEmail(m.email), m]));
  return parseRecipientEmails(raw).map((email) => ({
    email,
    member: byEmail.get(normalizeEmail(email)),
  }));
}

/** The signed-in user's own membership row, matched by id first then email. */
export function pickDefaultRecipientMember(
  members: TeamMember[],
  user: { id?: string; email?: string } | null | undefined,
): TeamMember | undefined {
  if (!user) return undefined;
  if (user.id) {
    const byId = members.find((m) => m.id === user.id);
    if (byId) return byId;
  }
  if (user.email) {
    const target = normalizeEmail(user.email);
    return members.find((m) => normalizeEmail(m.email) === target);
  }
  return undefined;
}

// The member list opens inside the job modal's `overflow-y-auto` box, so an
// `absolute` list that extends past the modal's content is CLIPPED — and because
// the recipients field sits at the bottom of the form there is nothing below it
// to scroll to, making the last members permanently unreachable. Measure the
// real clipping boundary and flip/shrink the list to fit inside it.
const MIN_LIST_HEIGHT = 140;
const MAX_LIST_HEIGHT = 240;

function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * A dropdown anchored under (or over) its trigger that stays inside whatever box
 * clips it — here, the job modal's scroll container.
 *
 * Closing is handled by a document-level listener rather than a `fixed inset-0`
 * overlay: the overlay sat above the trigger and did not scroll with the modal,
 * so once the form scrolled the click-catcher and the list were in different
 * places.
 */
function useAnchoredDropdown() {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState({ dropUp: false, maxHeight: MAX_LIST_HEIGHT });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const parent = getScrollParent(trigger);
    const parentRect = parent?.getBoundingClientRect();
    const top = parentRect ? parentRect.top : 0;
    const bottom = parentRect ? parentRect.bottom : window.innerHeight;
    const GAP = 8;
    const below = bottom - rect.bottom - GAP;
    const above = rect.top - top - GAP;
    const dropUp = below < MIN_LIST_HEIGHT && above > below;
    const space = dropUp ? above : below;
    setPlacement({
      dropUp,
      maxHeight: Math.max(MIN_LIST_HEIGHT, Math.min(MAX_LIST_HEIGHT, space)),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    // Bring the trigger fully into view first, so the measurement below sees the
    // space the list will actually get.
    triggerRef.current?.scrollIntoView?.({ block: 'nearest' });
    const raf = requestAnimationFrame(measure);
    const parent = getScrollParent(triggerRef.current);
    parent?.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      parent?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const listClass = `absolute z-20 w-full overflow-y-auto rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg ${
    placement.dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
  }`;

  return { open, setOpen, containerRef, triggerRef, listClass, maxHeight: placement.maxHeight };
}

// Report Generation config: pick a report type, choose delivery (Email and/or
// the Reports page), and — when Email is on — select which org members get it.
// Selected members' emails are written into `recipients` (what the backend
// sends to); `recipientIds` keeps the checkbox state on edit.
function ReportGenerationJobConfig({
  config,
  onChange,
  isCreate,
}: {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
  isCreate?: boolean;
}) {
  const { user } = useAuthStore();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const {
    open: showDropdown,
    setOpen: setShowDropdown,
    containerRef,
    triggerRef,
    listClass,
    maxHeight,
  } = useAnchoredDropdown();
  const appliedDefaultRef = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    teamMembersService
      .getTeamMembers({ status: 'active', limit: 200 })
      .then((res) => {
        if (active) setMembers(res.data || []);
      })
      .catch((err) => console.error('Failed to load team members:', err))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const update = (field: string, value: unknown) => onChange({ ...config, [field]: value });

  const deliverEmail = config.deliverEmail !== false; // default on
  const deliverUi = config.deliverUi === true;

  const memberName = (m: TeamMember) =>
    m.displayName || [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;

  const selection = useMemo(
    () => resolveRecipientSelection(config.recipients, members),
    [config.recipients, members],
  );
  const selectedEmails = useMemo(
    () => new Set(selection.map((r) => normalizeEmail(r.email))),
    [selection],
  );
  const orphans = selection.filter((r) => !r.member);

  // Write both keys from one list so they can never disagree. `recipientIds` is
  // kept only for backward compatibility with jobs saved by the old UI; nothing
  // reads it for delivery.
  const writeRecipients = useCallback(
    (emails: string[]) => {
      const byEmail = new Map(members.map((m) => [normalizeEmail(m.email), m]));
      const ids = emails
        .map((e) => byEmail.get(normalizeEmail(e))?.id)
        .filter((id): id is string => Boolean(id));
      onChange({ ...config, recipients: emails.join(', '), recipientIds: ids });
    },
    [config, members, onChange],
  );

  const toggleMember = (member: TeamMember) => {
    const current = selection.map((r) => r.email);
    const next = selectedEmails.has(normalizeEmail(member.email))
      ? current.filter((e) => normalizeEmail(e) !== normalizeEmail(member.email))
      : [...current, member.email];
    writeRecipients(next);
  };

  const removeRecipient = (email: string) =>
    writeRecipients(
      selection.map((r) => r.email).filter((e) => normalizeEmail(e) !== normalizeEmail(email)),
    );

  // On create, start with the admin creating the job already selected — they can
  // uncheck themselves. Runs once, and only while the list is still untouched,
  // so it can't re-add someone who deliberately removed themselves.
  useEffect(() => {
    if (!isCreate || appliedDefaultRef.current || loading || members.length === 0) return;
    if (parseRecipientEmails(config.recipients).length > 0) {
      appliedDefaultRef.current = true;
      return;
    }
    appliedDefaultRef.current = true;
    const me = pickDefaultRecipientMember(members, user);
    if (me) onChange({ ...config, recipients: me.email, recipientIds: [me.id] });
  }, [isCreate, loading, members, config, onChange, user]);

  const buttonLabel =
    selection.length === 0
      ? 'Select members...'
      : `${selection.length} recipient${selection.length === 1 ? '' : 's'} selected`;

  return (
    <div className="mt-3 space-y-3">
      <ConfigField label="Report Type *">
        <ThemedSelect
          className={selectClass}
          value={(config.reportType as string) || ''}
          onChange={(e) => update('reportType', e.target.value)}
        >
          <option value="">Select report type...</option>
          {REPORT_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </ThemedSelect>
      </ConfigField>

      <ConfigField label="Send report as *">
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-gray-300 dark:border-gray-600"
              checked={deliverEmail}
              onChange={(e) => update('deliverEmail', e.target.checked)}
            />
            Email
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-gray-300 dark:border-gray-600"
              checked={deliverUi}
              onChange={(e) => update('deliverUi', e.target.checked)}
            />
            Show on Reports page
          </label>
        </div>
      </ConfigField>

      {deliverEmail && (
        <ConfigField label="Send to (organization members) *">
          <div className="relative" ref={containerRef}>
            <button
              type="button"
              ref={triggerRef}
              onClick={() => setShowDropdown((s) => !s)}
              className={`${selectClass} flex items-center justify-between text-left`}
            >
              <span className={selection.length ? '' : 'text-gray-400 dark:text-gray-500'}>
                {loading ? 'Loading members...' : buttonLabel}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''} dark:text-gray-500`}
              />
            </button>
            {showDropdown && (
              <div role="listbox" aria-label="Report recipients" style={{ maxHeight }} className={listClass}>
                {loading ? (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Loading members...
                  </div>
                ) : members.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No active members found.
                  </div>
                ) : (
                  members.map((m) => (
                    <label
                      key={m.id}
                      className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 dark:border-gray-600"
                        checked={selectedEmails.has(normalizeEmail(m.email))}
                        onChange={() => toggleMember(m)}
                      />
                      <span className="text-gray-900 dark:text-white">
                        {memberName(m)}
                        {(m.id === user?.id || m.email === user?.email) && (
                          <span className="text-xs text-gray-400 dark:text-gray-500"> (you)</span>
                        )}
                      </span>
                      <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">{m.email}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Every address that will actually be mailed, shown in full. The old
              truncated "Name +1" label is what let a stale recipient hide. */}
          {selection.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {selection.map((r) => (
                <span
                  key={normalizeEmail(r.email)}
                  title={r.member ? memberName(r.member) : 'No longer an active organization member'}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
                    r.member
                      ? 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-200'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                  }`}
                >
                  {!r.member && <AlertTriangle className="h-3 w-3" />}
                  {r.email}
                  <button
                    type="button"
                    onClick={() => removeRecipient(r.email)}
                    aria-label={`Remove ${r.email}`}
                    className="ml-0.5 text-gray-400 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {orphans.length > 0 && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              {orphans.length} address{orphans.length === 1 ? '' : 'es'} no longer belong to an
              active member but{' '}
              {orphans.length === 1 ? 'is' : 'are'} still being emailed this report. Remove{' '}
              {orphans.length === 1 ? 'it' : 'them'} above, then save.
            </p>
          )}
        </ConfigField>
      )}
    </div>
  );
}

export default function JobConfigPanel({ jobType, config, onChange, isCreate }: JobConfigPanelProps) {
  const update = (field: string, value: unknown) => {
    onChange({ ...config, [field]: value });
  };

  switch (jobType) {
    case ScheduledJobType.AUTOMATION_RULE:
      return <AutomationRuleJobConfig config={config} onChange={onChange} />;

    case ScheduledJobType.N8N_WORKFLOW:
      return (
        <N8nWorkflowJobConfig
          config={config}
          onChange={onChange}
        />
      );

    case ScheduledJobType.SLA_CHECK:
      return (
        <div className="mt-3 space-y-2">
          <ConfigField label="SLA Policy ID (optional)">
            <input
              className={inputClass}
              type="text"
              placeholder="Leave empty to check all policies"
              value={(config.slaPolicyId as string) || ''}
              onChange={(e) => update('slaPolicyId', e.target.value)}
            />
          </ConfigField>
        </div>
      );

    case ScheduledJobType.STALE_TICKET_CHECK:
      return (
        <div className="mt-3 space-y-2">
          <ConfigField label="Stale After (hours) *">
            <input
              className={inputClass}
              type="number"
              min={1}
              placeholder="24"
              value={(config.staleAfterHours as number) || ''}
              onChange={(e) => update('staleAfterHours', parseInt(e.target.value) || 0)}
            />
          </ConfigField>
          <ConfigField label="Statuses to check">
            <div className="flex flex-wrap gap-3">
              {STALE_STATUS_OPTIONS.map((opt) => {
                const selected = parseStatusList(config.statuses);
                const isChecked = selected.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 dark:border-gray-600"
                      checked={isChecked}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...selected, opt.value]
                          : selected.filter((s) => s !== opt.value);
                        update('statuses', next.join(', '));
                      }}
                    />
                    {opt.label}
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Leave all unchecked to check every active ticket (new, in progress, pending).
            </p>
          </ConfigField>
          <ConfigField label="Action">
            <ThemedSelect
              className={selectClass}
              value={(config.action as string) || 'notify'}
              onChange={(e) => update('action', e.target.value)}
            >
              <option value="notify">Notify</option>
              <option value="close">Close Ticket</option>
              <option value="escalate">Escalate</option>
            </ThemedSelect>
          </ConfigField>
        </div>
      );

    case ScheduledJobType.REPORT_GENERATION:
      return <ReportGenerationJobConfig config={config} onChange={onChange} isCreate={isCreate} />;

    case ScheduledJobType.CLEANUP:
      return (
        <div className="mt-3 space-y-2">
          <ConfigField label="Target *">
            <ThemedSelect
              className={selectClass}
              value={(config.target as string) || 'executions'}
              onChange={(e) => update('target', e.target.value)}
            >
              <option value="executions">Execution Logs</option>
              <option value="logs">System Logs</option>
              <option value="notifications">Old Notifications</option>
            </ThemedSelect>
          </ConfigField>
          <ConfigField label="Retention Days *">
            <input
              className={inputClass}
              type="number"
              min={1}
              placeholder="30"
              value={(config.retentionDays as number) || ''}
              onChange={(e) => update('retentionDays', parseInt(e.target.value) || 0)}
            />
          </ConfigField>
        </div>
      );

    case ScheduledJobType.CUSTOM:
      return (
        <div className="mt-3 space-y-2">
          <ConfigField label="Webhook URL *">
            <input
              className={inputClass}
              type="text"
              placeholder="https://example.com/webhook"
              value={(config.webhookUrl as string) || ''}
              onChange={(e) => update('webhookUrl', e.target.value)}
            />
          </ConfigField>
          <ConfigField label="Method">
            <ThemedSelect
              className={selectClass}
              value={(config.method as string) || 'POST'}
              onChange={(e) => update('method', e.target.value)}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
            </ThemedSelect>
          </ConfigField>
          <ConfigField label="Payload (JSON)">
            <textarea
              className={textareaClass}
              rows={3}
              placeholder='{"key": "value"}'
              value={
                typeof config.payload === 'string'
                  ? config.payload
                  : JSON.stringify(config.payload || {}, null, 2)
              }
              onChange={(e) => {
                try {
                  update('payload', JSON.parse(e.target.value));
                } catch {
                  update('payload', e.target.value);
                }
              }}
            />
          </ConfigField>
        </div>
      );

    default:
      return null;
  }
}
